import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, CheckCircle, Loader2 } from 'lucide-react';
import { useTaskTimer } from './useTaskTimer';
import { FinishTaskDialog } from './FinishTaskDialog';

interface TaskStopwatchProps {
  taskId: string;
  phaseStatus: string;
  assigneeId: string | null;
  timerStartedAt: string | null;
  accumulatedSeconds: number;
  currentUserId: string;
  compact?: boolean;
}

function formatTime(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

export function TaskStopwatch({
  taskId,
  phaseStatus,
  assigneeId,
  timerStartedAt,
  accumulatedSeconds,
  currentUserId,
  compact = false,
}: TaskStopwatchProps) {
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);

  const {
    displaySeconds,
    isRunning,
    isPaused,
    hasStarted,
    isLoading,
    isStarting,
    isPausing,
    isResuming,
    isFinishing,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
  } = useTaskTimer({
    taskId,
    initialTimerStartedAt: timerStartedAt,
    initialAccumulatedSeconds: accumulatedSeconds,
    initialPhaseStatus: phaseStatus,
  });

  // Only show for the assigned user
  if (currentUserId !== assigneeId) {
    return null;
  }

  // Don't show for completed tasks
  if (phaseStatus === 'done') {
    return null;
  }

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-4"
        onClick={(e) => e.stopPropagation()}
      >
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isAnyLoading = isStarting || isPausing || isResuming || isFinishing;

  if (compact) {
    return (
      <>
        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <span className="text-sm font-mono tabular-nums">
            {formatTime(displaySeconds)}
          </span>
          {isRunning && (
            <span className="text-[10px] text-green-600 font-medium animate-pulse">
              Running
            </span>
          )}
          {isPaused && (
            <span className="text-[10px] text-yellow-600 font-medium">
              Paused
            </span>
          )}

          {!hasStarted ? (
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                startTimer();
              }}
              disabled={isAnyLoading}
            >
              {isStarting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Play className="h-3 w-3" />
              )}
            </Button>
          ) : isRunning ? (
            <>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  pauseTimer();
                }}
                disabled={isAnyLoading}
              >
                {isPausing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Pause className="h-3 w-3" />
                )}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-green-600 hover:text-green-700"
                onClick={(e) => {
                  e.stopPropagation();
                  setFinishDialogOpen(true);
                }}
                disabled={isAnyLoading}
              >
                {isFinishing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <CheckCircle className="h-3 w-3" />
                )}
              </Button>
            </>
          ) : isPaused ? (
            <>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  resumeTimer();
                }}
                disabled={isAnyLoading}
              >
                {isResuming ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Play className="h-3 w-3" />
                )}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-green-600 hover:text-green-700"
                onClick={(e) => {
                  e.stopPropagation();
                  setFinishDialogOpen(true);
                }}
                disabled={isAnyLoading}
              >
                {isFinishing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <CheckCircle className="h-3 w-3" />
                )}
              </Button>
            </>
          ) : null}
        </div>

        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <FinishTaskDialog
            open={finishDialogOpen}
            onOpenChange={setFinishDialogOpen}
            onConfirm={(deliverables) => {
              stopTimer(deliverables);
              setFinishDialogOpen(false);
            }}
            isLoading={isFinishing}
          />
        </div>
      </>
    );
  }

  // Full / card layout
  return (
    <>
      <div
        className="flex items-center justify-between gap-4 pt-4 border-t"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <div className="text-2xl font-mono font-semibold tabular-nums">
            {formatTime(displaySeconds)}
          </div>
          {isRunning && (
            <span className="text-xs text-green-600 font-medium animate-pulse">
              Running
            </span>
          )}
          {isPaused && (
            <span className="text-xs text-yellow-600 font-medium">Paused</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!hasStarted ? (
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                startTimer();
              }}
              disabled={isAnyLoading}
              className="gap-1"
            >
              {isStarting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              Start
            </Button>
          ) : isRunning ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  pauseTimer();
                }}
                disabled={isAnyLoading}
                className="gap-1"
              >
                {isPausing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Pause className="h-4 w-4" />
                )}
                Pause
              </Button>
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setFinishDialogOpen(true);
                }}
                disabled={isAnyLoading}
                className="gap-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {isFinishing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                Finish
              </Button>
            </>
          ) : isPaused ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  resumeTimer();
                }}
                disabled={isAnyLoading}
                className="gap-1"
              >
                {isResuming ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                Resume
              </Button>
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setFinishDialogOpen(true);
                }}
                disabled={isAnyLoading}
                className="gap-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {isFinishing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                Finish
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <FinishTaskDialog
          open={finishDialogOpen}
          onOpenChange={setFinishDialogOpen}
          onConfirm={(deliverables) => {
            stopTimer(deliverables);
            setFinishDialogOpen(false);
          }}
          isLoading={isFinishing}
        />
      </div>
    </>
  );
}
